import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../utils/authCookies";

interface PONumberProps {
  title?: string;
  value?: string;
  placeholder?: string;
  orderId?: string;
  orderType?: "sales" | "installation" | "removal";
  onSaved?: (value: string) => void | Promise<void>;
  /** Create screens: input is always open; no pencil / Cancel / Save. */
  mode?: "edit" | "create";
  onChange?: (value: string) => void;
  className?: string;
  headerBgColor?: string;
  /** Linked-from-project create: show the card but lock the input, no edit. */
  disabled?: boolean;
}

const PONumber: React.FC<PONumberProps> = ({
  title = "PO Number",
  value = "",
  placeholder = "e.g. PO-2026-00124",
  orderId,
  orderType,
  onSaved,
  mode = "edit",
  onChange,
  className = "",
  headerBgColor,
  disabled = false,
}) => {
  const isCreateMode = mode === "create";
  const isLocked = Boolean(disabled);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [initialValue, setInitialValue] = useState(value);

  useEffect(() => {
    setDraft(value);
    setInitialValue(value);
  }, [value]);

  const hasChanges = useMemo(() => draft.trim() !== initialValue.trim(), [draft, initialValue]);

  const handleEdit = () => {
    setIsEditing(true);
    setDraft(value || "");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraft(initialValue);
  };

  const handleSave = async () => {
    const next = draft.trim();
    const token = getAuthCookie("sessionToken") || "";
    const apiBaseUrl = process.env.REACT_APP_API_URL || "";

    if (!orderId || !orderType) {
      showError("Missing order details for PO Number update.");
      return;
    }

    await axios.patch(
      `${apiBaseUrl}/po-number/orders/${orderId}`,
      {
        order_type: orderType,
        po_number: next,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    showSuccess("PO Number updated successfully!");

    await onSaved?.(next);
    setInitialValue(next);
    setIsEditing(false);
  };

  return (
    <div className={`card shadow-lg shadow-border-1 ${className}`}>
      <div
        className="py-2 px-3 rounded-top po-number-header-blue"
        style={{ height: "54px", ...(headerBgColor ? { backgroundColor: headerBgColor } : {}) }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 d-flex align-items-center">
            <i className="ri-file-list-3-line fs-20" aria-hidden="true" />
            <span className="ms-2 fs-17">{title}</span>
          </h6>

          {!isCreateMode && !isLocked ? (
          <div className="d-flex align-items-center gap-2">
            {!isEditing ? (
              <button
                type="button"
                className="bg-white border border-2 btn btn-link p-0 px-1 text-muted"
                onClick={handleEdit}
                title="Edit PO Number"
              >
                <i className="fs-20 ri-edit-line text-black" />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-light border px-3 py-2 br-8"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary table-addbutton px-3 py-2 br-8"
                  onClick={async () => {
                    try {
                      await handleSave();
                    } catch (error: any) {
                      showError(`Error updating PO Number: ${error?.message || "Unknown error"}`);
                    }
                  }}
                  disabled={!hasChanges}
                >
                  Save
                </button>
              </>
            )}
          </div>
          ) : null}
        </div>
      </div>

      <div className="card-body body-box">
        <input
          type="text"
          className={`form-control br-8 ${
            !isLocked && (isCreateMode || isEditing) ? "bg-white text-dark" : "text-black po-number-readonly"
          }`}
          value={draft}
          placeholder={placeholder}
          disabled={isLocked || (!isCreateMode && !isEditing)}
          style={isLocked || (!isCreateMode && !isEditing) ? { cursor: "not-allowed", opacity: 1 } : undefined}
          onChange={(e) => {
            if (isLocked) return;
            const next = e.target.value;
            setDraft(next);
            if (isCreateMode) onChange?.(next);
          }}
          onKeyDown={(e) => {
            if (isLocked || isCreateMode) return;
            if (e.key === "Escape") handleCancel();
            if (e.key === "Enter" && hasChanges) {
              void (async () => {
                try {
                  await handleSave();
                } catch (error: any) {
                  showError(`Error updating PO Number: ${error?.message || "Unknown error"}`);
                }
              })();
            }
          }}
          autoFocus={!isLocked && !isCreateMode && isEditing}
        />
      </div>
      <style>
        {`
          .po-number-title-blue {
            color: #155dfc;
          }
          .po-number-header-blue {
            background-color: #eff6ff;
            border-radius: 19px 19px 0 0;
          }

          .body-box{
          border-radius: 0px 0px 19px 19px !important;
          }
          .po-number-readonly {
            background-color: #f2f2f2 !important;
          }
        `}
      </style>
    </div>
  );
};

export default PONumber;
